import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BoxArrowUpRight } from '@styled-icons/bootstrap/BoxArrowUpRight';
import Filter from 'Components/Filter/Filter';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import ScrollableTableContainer from 'Components/ScrollableTableContainer';
import { EmptyStateCard, ThemeButton } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { ECompanyStatus } from 'FeatureModules/Auth/types/company.types';
import DocumentSkeleton from 'FeatureModules/Document/Components/DocumentSkeleton';
import DocumentTable from 'FeatureModules/Document/Components/Tables/DocumentTable';
import { IDocumentTableBodyAction } from 'FeatureModules/Document/Components/Tables/DocumentTableBody';
import { EDocumentsTableColumnKeys } from 'FeatureModules/Document/utils/documentHeaderColumns';
import { IBaseDocument } from 'FeatureModules/Document/types/document.types';
import { GET_BASE_PARTNER_COMPANY } from 'graphql/secure/queries/partner.queries';
import usePartnerDocuments from 'FeatureModules/Partner/hooks/usePartnerDocuments';
import React, { FC, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { TRouteParamsWithId } from 'types/router.types';

const StyledInfiniteScrollWrapper = styled(InfiniteScrollWrapper)(({ theme }) => ({
  margin: theme.spacing(0, -2),
  padding: theme.spacing(0, 2),
  overflow: 'auto',
}));

const PartnerDocuments: FC = () => {
  const {
    documents,
    selectedDocumentIds,
    error,
    loading,
    searchTerm,
    hasNextPage,
    allowBulkDeletion,
    filters,
    handleFilterChange,
    handleFilterReset,
    handleSearchTermChange,
    handlePageEndReached,
    setSelectedDocumentIds,
    handleDeleteSelectedDocuments,
    handleCreateDocument,
  } = usePartnerDocuments();
  const navigate = useNavigate();
  const { id: partnerId } = useParams<TRouteParamsWithId>();

  const { data: partnerData } = useQuery<{ partner: IBasePartnerCompany }>(
    GET_BASE_PARTNER_COMPANY,
    {
      variables: {
        id: partnerId,
      },
      skip: !partnerId,
    }
  );

  const isPartnerRegistered = partnerData?.partner.status === ECompanyStatus.REGISTERED;

  const documentTableActions: IDocumentTableBodyAction[] = [
    {
      icon: <BoxArrowUpRight size="15" />,
      tooltip: 'Open in new tab',
      onClick: (document: IBaseDocument) => window.open(document.url, '_blank'),
      dataTestId: 'download-document-btn',
    },
  ];

  /**
   * Route to main overview and open create new request dialog
   */
  const handleNewRequestClick = () => {
    if (partnerId) {
      navigate(`/partners/${partnerId}?createNewRequest=1`);
    } else {
      console.error('No partner id found');
    }
  };

  const openDocumentOverview = (document: IBaseDocument) => {
    navigate(`/documents/shared/${document.id}`);
  };

  if (error) {
    return <ErrorState />;
  }

  if (loading) {
    return <Loader />;
  }

  if (!documents.length && !searchTerm) {
    return (
      <PageContainer>
        <PageTitle
          title="Partner documents"
          titleExtension={partnerData?.partner.name}
          goBackLabel="All partners"
          goBackUrl="/partners"
        />
        <Box mb={3} />
        <EmptyStateCard
          title="Add documents to your sites to keep track of important information like proof of no deforestation"
          variant={EItemTypes.DOCUMENT}
          secondaryButtonText={isPartnerRegistered ? 'Add new document' : undefined}
          buttonText={isPartnerRegistered ? 'Request new document' : 'Add new document'}
          buttonIcon={
            isPartnerRegistered ? (
              <Icon name="document-request-pending" />
            ) : (
              <Icon name="document-request-add" />
            )
          }
          secondaryButtonIcon={<Icon name="document-request-add" />}
          background={EBackgroundTypes.DETAILED_DOCUMENTS}
          size={EAvailableSizes.LARGE}
          onCreateItemClick={isPartnerRegistered ? handleNewRequestClick : handleCreateDocument}
          onClickSecondaryButton={handleCreateDocument}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle
        title="Partner documents"
        goBackLabel="All partners"
        titleExtension={partnerData?.partner.name}
        goBackUrl="/partners"
      />
      <Box mb={3} />

      <Filter
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterReset={handleFilterReset}
      />

      <Box position="relative">
        <ActionContainer>
          <SearchToolbar
            setSearchTerm={handleSearchTermChange}
            searchTerm={searchTerm}
            data-testid="partner-document-search-input"
            placeholder="Search document"
            className="search-input"
            actions={
              <FlexBox ml={1} gap={1}>
                {isPartnerRegistered && (
                  <ThemeButton
                    startIcon={<Icon name="document-request-pending" color="WHITE" />}
                    onClick={handleNewRequestClick}
                  >
                    New request
                  </ThemeButton>
                )}
                <ThemeButton
                  color="WHITE"
                  startIcon={<Icon name="document-request-add" />}
                  onClick={handleCreateDocument}
                >
                  Add new document
                </ThemeButton>

                {selectedDocumentIds.length !== 0 && (
                  <ThemeButton
                    size="medium"
                    color="WHITE"
                    tooltip={'Documents are used as proof in impact claims'}
                    disabled={!allowBulkDeletion}
                    startIcon={<Icon name="delete" size="small" />}
                    onClick={handleDeleteSelectedDocuments}
                  >
                    Delete {selectedDocumentIds.length}
                  </ThemeButton>
                )}
              </FlexBox>
            }
          />
        </ActionContainer>

        <StyledInfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
          <ScrollableTableContainer>
            <DocumentTable
              columns={[
                EDocumentsTableColumnKeys.TITLE,
                EDocumentsTableColumnKeys.CREATOR,
                EDocumentsTableColumnKeys.USAGE,
                EDocumentsTableColumnKeys.ISSUE_DATE,
                EDocumentsTableColumnKeys.EXPIRY_DATE,
                EDocumentsTableColumnKeys.ACTIONS,
              ]}
              documents={documents}
              actions={documentTableActions}
              selectedDocumentIds={selectedDocumentIds}
              onDocumentClick={openDocumentOverview}
              onSelectionChange={setSelectedDocumentIds}
            />
          </ScrollableTableContainer>
        </StyledInfiniteScrollWrapper>
      </Box>

      {!documents?.length ? (
        <EmptySearchResults
          message="No search results found"
          skeletonComponent={<DocumentSkeleton title={searchTerm} />}
        />
      ) : (
        <Fragment />
      )}
    </PageContainer>
  );
};

export default PartnerDocuments;
